import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface MedidasCorporales {
  altura: string | null;
  talla: string | null;
  contorno_pecho: string | null;
  contorno_cintura: string | null;
  contorno_cadera: string | null;
  contorno_brazo: string | null;
  largo_pierna: string | null;
  notas_medidas: string | null;
  tallas_por_marca: Record<string, string> | null;
}

export interface CombinacionPersonalizada {
  base: string;
  complemento: string;
  tono: string;
  desc: string;
}

export interface ClientePerfil {
  id: number;
  cliente_id: number;
  paleta_colores: string[];
  tono_piel: string | null;
  estilo_personal: string | null;
  notas_estilista: string | null;
  ocasiones_preferidas: string[] | null;
  prendas_a_evitar: string[] | null;
  medidas_corporales: MedidasCorporales | null;
  looks_favoritos: string[] | null;
  combinaciones_personalizadas: CombinacionPersonalizada[] | null;
  created_at?: string;
  updated_at?: string;
}

export interface UpsertResult {
  success: boolean;
  data?: ClientePerfil;
  error?: string;
}

const COLORS_DEFAULT = {
  'Blanco': '#f5f5f5',
  'Negro': '#1a1a1a',
  'Azul marino': '#1e3a5f',
  'Gris perla': '#c0c0c0',
  'Beige': '#e8dcc8',
  'Azul eléctrico': '#2563eb',
  'Burdeos': '#800020',
  'Tweed marrón': '#8b6914',
  'Crudo': '#fdf5e6',
  'Verde bosque': '#2f4f2f',
  'Terracota': '#c45c30',
  'Rosa pálido': '#e6b3c8',
  'Oro': '#c5a059',
  'Plata': '#a0a0a0',
  'Cobre': '#b87333',
} as const;

export function getColorHex(nombre: string): string {
  return COLORS_DEFAULT[nombre as keyof typeof COLORS_DEFAULT] || '#e5e7eb';
}

export function isColorLight(hex: string): boolean {
  const lightColors = ['#f5f5f5', '#fdf5e6', '#e8dcc8', '#e6b3c8'];
  return lightColors.includes(hex);
}

export function useAdminClientePerfil(clienteId: number | undefined) {
  const [perfil, setPerfil] = useState<ClientePerfil | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPerfil = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('perfiles_cliente')
        .select('*')
        .eq('cliente_id', clienteId)
        .maybeSingle();
      if (err) {
        setError(err.message);
      } else if (rows) {
        setPerfil(rows as ClientePerfil);
      } else {
        setPerfil(null);
      }
    } catch {
      setError('Error de conexión al cargar perfil');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchPerfil();
  }, [fetchPerfil]);

  const upsert = useCallback(async (
    updates: Partial<Omit<ClientePerfil, 'id' | 'cliente_id' | 'created_at' | 'updated_at'>>
  ): Promise<UpsertResult> => {
    if (!clienteId) {
      return { success: false, error: 'No hay cliente seleccionado' };
    }
    setLoading(true);
    setError(null);
    try {
      const { data: existing, error: existingErr } = await supabase
        .from('perfiles_cliente')
        .select('id')
        .eq('cliente_id', clienteId)
        .maybeSingle();

      if (existingErr) {
        throw new Error(existingErr.message);
      }

      const now = new Date().toISOString();

      if (existing) {
        const { data: updated, error: err } = await supabase
          .from('perfiles_cliente')
          .update({ ...updates, updated_at: now })
          .eq('cliente_id', clienteId)
          .select('*');

        if (err) {
          throw new Error(err.message);
        }

        if (updated && updated.length > 0) {
          const p = updated[0] as ClientePerfil;
          setPerfil(p);
          return { success: true, data: p };
        }
        return { success: false, error: 'No se devolvieron datos tras actualizar' };
      } else {
        const { data: inserted, error: err } = await supabase
          .from('perfiles_cliente')
          .insert({ cliente_id: clienteId, ...updates })
          .select('*');

        if (err) {
          throw new Error(err.message);
        }

        if (inserted && inserted.length > 0) {
          const p = inserted[0] as ClientePerfil;
          setPerfil(p);
          return { success: true, data: p };
        }
        return { success: false, error: 'No se devolvieron datos tras crear' };
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al guardar perfil';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  return { perfil, setPerfil, upsert, refresh: fetchPerfil, loading, error };
}